import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import styled from 'styled-components';
import GoogleMap from 'google-map-react';
import iconZoomIn from 'assets/customerInsights/ci-map-zoom-in.svg';
import iconZoomOut from 'assets/customerInsights/ci-map-zoom-out.svg';
import storePinIcon from 'assets/location-marker.svg';
import {
  SEGMENTS,
  SEGMENT_DEFINITIONS,
  LOCATIONS_MAP_MIN_CUSTOMERS
} from '../constants';
import {LocationsSection, LocationsGrid, SectionTitle} from '../styles';

const DEFAULT_CENTER = {lat: 40.7549, lng: -73.984};
const DEFAULT_ZOOM = 11;
const MIN_MARKER = 28;
const MAX_MARKER = 72;

const SEGMENT_PILLS = [
  {id: SEGMENTS.ALL, label: 'All'},
  {id: SEGMENTS.NEW, label: SEGMENT_DEFINITIONS.newCustomers.label},
  {
    id: SEGMENTS.OCCASIONAL,
    label: SEGMENT_DEFINITIONS.occasionalCustomers.label
  },
  {id: SEGMENTS.FREQUENT, label: SEGMENT_DEFINITIONS.frequentCustomers.label}
];

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const Tabs = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid #d9d9d9;
  padding-bottom: 19px;
`;

const Tab = styled.button`
  position: relative;
  border: none;
  background: transparent;
  padding: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.2px;
  color: #2f3d48;
  cursor: pointer;

  &.isActive {
    font-weight: 700;
  }

  &.isActive::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -21px;
    height: 2px;
    background: #2f3d48;
  }
`;

const Description = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  color: #172751;
  margin: 20px 11px 20px 0;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: calc(5 * 73px + 4 * 4px);
  overflow-y: auto;
  padding-right: 4px;

  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  transition: scrollbar-color 0.2s ease;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 3px;
    transition: background 0.2s ease;
  }

  &:hover {
    scrollbar-color: #d9d9d9 transparent;

    &::-webkit-scrollbar-thumb {
      background: #d9d9d9;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: #b0b3b8;
    }
  }
`;

const EmptyText = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(70, 113, 219, 0.1);
  border-radius: 10px;
  padding: 14px 20px;
  min-height: 73px;
`;

const RowLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const RowPostal = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 150%;
  color: #172751;
`;

const RowCity = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  color: #172751;
`;

const RowCount = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 22px;
  font-weight: 700;
  line-height: 150%;
  color: #172751;
  text-align: right;
`;

const MapContainer = styled.div`
  border-radius: 10px;
  min-height: 502px;
  overflow: hidden;
  position: relative;
  background: #eef2f7;
`;

const StorePin = styled.img`
  position: absolute;
  transform: translate(-50%, -100%);
  width: 30px;
  height: 40px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.35));
  pointer-events: none;
  z-index: 2;
`;

const ZipMarker = styled.div`
  position: absolute;
  transform: translate(-50%, -50%);
  width: ${({$size}) => $size}px;
  height: ${({$size}) => $size}px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(240, 40, 40, 0.95) 0%,
    rgba(240, 40, 40, 0.9) 45%,
    rgba(240, 40, 40, 0.45) 75%,
    rgba(240, 40, 40, 0.15) 90%,
    rgba(240, 40, 40, 0) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: ${({$size}) => Math.max(13, Math.floor($size / 2.5))}px;
  line-height: 100%;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  pointer-events: none;
`;

const MapFooter = styled.div`
  position: absolute;
  left: 8px;
  bottom: 8px;
  background: #ffffff;
  padding: 5px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  color: #000000;
  box-shadow: 0px 8px 16px 0px #00000032;
`;

const ZoomControls = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ZoomButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: block;

  img {
    display: block;
    width: 28px;
    height: 28px;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15));
  }
`;

const selectSegmentCount = (location, segment) => {
  if (segment === SEGMENTS.ALL) return location.customerCount ?? 0;
  return location.segments?.[segment] ?? 0;
};

const markerSize = (activeCount, maxCount) => {
  if (!maxCount) return MIN_MARKER;
  const ratio = Math.min(1, activeCount / maxCount);
  return Math.round(MIN_MARKER + ratio * (MAX_MARKER - MIN_MARKER));
};

const FIT_BOUNDS_PADDING = 48;
const FIT_BOUNDS_MAX_ZOOM = 14;

const CustomerLocationsMap = ({locations, storeLocation}) => {
  const [activeSegment, setActiveSegment] = useState(SEGMENTS.ALL);
  const mapRef = useRef(null);
  const mapsRef = useRef(null);

  const visible = useMemo(() => {
    const rows = (locations || [])
      .map((loc) => ({
        ...loc,
        activeCount: selectSegmentCount(loc, activeSegment)
      }))
      .filter((loc) => loc.activeCount >= LOCATIONS_MAP_MIN_CUSTOMERS)
      .sort((a, b) => b.activeCount - a.activeCount);
    return rows;
  }, [locations, activeSegment]);

  const plottable = useMemo(
    () =>
      visible.filter(
        (loc) =>
          loc.centroid &&
          typeof loc.centroid.lat === 'number' &&
          typeof loc.centroid.lng === 'number'
      ),
    [visible]
  );

  const maxCount = useMemo(
    () => plottable.reduce((acc, loc) => Math.max(acc, loc.activeCount), 0),
    [plottable]
  );

  const initialCenter = useMemo(() => {
    if (storeLocation?.lat != null && storeLocation?.lng != null) {
      return {lat: storeLocation.lat, lng: storeLocation.lng};
    }
    if (plottable.length > 0) {
      return {lat: plottable[0].centroid.lat, lng: plottable[0].centroid.lng};
    }
    return DEFAULT_CENTER;
  }, [storeLocation, plottable]);

  const fitToPlottable = useCallback(() => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    if (!map || !maps || plottable.length === 0) return;
    const bounds = new maps.LatLngBounds();
    plottable.forEach((loc) => {
      bounds.extend({lat: loc.centroid.lat, lng: loc.centroid.lng});
    });
    if (storeLocation?.lat != null && storeLocation?.lng != null) {
      bounds.extend({lat: storeLocation.lat, lng: storeLocation.lng});
    }
    map.fitBounds(bounds, FIT_BOUNDS_PADDING);
    maps.event.addListenerOnce(map, 'idle', () => {
      if (map.getZoom() > FIT_BOUNDS_MAX_ZOOM) map.setZoom(FIT_BOUNDS_MAX_ZOOM);
    });
  }, [plottable, storeLocation]);

  const handleGoogleApiLoaded = ({map, maps}) => {
    mapRef.current = map;
    mapsRef.current = maps;
    fitToPlottable();
  };

  useEffect(() => {
    fitToPlottable();
  }, [fitToPlottable]);

  const handleZoom = (delta) => {
    const map = mapRef.current;
    if (!map || typeof map.getZoom !== 'function') return;
    map.setZoom(map.getZoom() + delta);
  };

  return (
    <LocationsSection>
      <SectionTitle>Customer Locations</SectionTitle>
      <LocationsGrid>
        <LeftColumn>
          <Tabs>
            {SEGMENT_PILLS.map((p) => (
              <Tab
                key={p.id}
                type='button'
                className={cn({isActive: activeSegment === p.id})}
                onClick={() => setActiveSegment(p.id)}
              >
                {p.label}
              </Tab>
            ))}
          </Tabs>
          <Description>
            Shows customer locations when at least {LOCATIONS_MAP_MIN_CUSTOMERS}{' '}
            customers order from the same zip code.
          </Description>
          <List>
            {visible.length === 0 && (
              <EmptyText>No zip codes match the current filter.</EmptyText>
            )}
            {visible.map((loc) => (
              <Row key={loc.postalCode}>
                <RowLeft>
                  <RowPostal>{loc.postalCode}</RowPostal>
                  <RowCity>{loc.areaName || loc.city}</RowCity>
                </RowLeft>
                <RowCount>{loc.activeCount}</RowCount>
              </Row>
            ))}
          </List>
        </LeftColumn>
        <MapContainer>
          <GoogleMap
            yesIWantToUseGoogleMapApiInternals
            defaultCenter={initialCenter}
            defaultZoom={DEFAULT_ZOOM}
            options={{
              disableDefaultUI: true,
              gestureHandling: 'greedy',
              clickableIcons: false
            }}
            onGoogleApiLoaded={handleGoogleApiLoaded}
          >
            {plottable.map((loc) => (
              <ZipMarker
                key={loc.postalCode}
                lat={loc.centroid.lat}
                lng={loc.centroid.lng}
                $size={markerSize(loc.activeCount, maxCount)}
              >
                {loc.activeCount}
              </ZipMarker>
            ))}
            {storeLocation?.lat != null && storeLocation?.lng != null && (
              <StorePin
                src={storePinIcon}
                alt='Your store'
                lat={storeLocation.lat}
                lng={storeLocation.lng}
              />
            )}
          </GoogleMap>
          <ZoomControls>
            <ZoomButton
              type='button'
              aria-label='Zoom in'
              onClick={() => handleZoom(1)}
            >
              <img src={iconZoomIn} alt='' />
            </ZoomButton>
            <ZoomButton
              type='button'
              aria-label='Zoom out'
              onClick={() => handleZoom(-1)}
            >
              <img src={iconZoomOut} alt='' />
            </ZoomButton>
          </ZoomControls>
          <MapFooter>Customer density by zip code</MapFooter>
        </MapContainer>
      </LocationsGrid>
    </LocationsSection>
  );
};

CustomerLocationsMap.propTypes = {
  storeLocation: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number
  }),
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      postalCode: PropTypes.string.isRequired,
      city: PropTypes.string,
      areaName: PropTypes.string,
      stateCode: PropTypes.string,
      countryCode: PropTypes.string,
      customerCount: PropTypes.number,
      orderCount: PropTypes.number,
      centroid: PropTypes.shape({
        lat: PropTypes.number,
        lng: PropTypes.number
      }),
      segments: PropTypes.shape({
        newCustomers: PropTypes.number,
        occasionalCustomers: PropTypes.number,
        frequentCustomers: PropTypes.number
      })
    })
  )
};

CustomerLocationsMap.defaultProps = {
  locations: [],
  storeLocation: null
};

export default CustomerLocationsMap;
